/**
 * Ops map zoom / home policy.
 * Esri **World Imagery** (satellite) on ``server.arcgisonline.com``: native LOD through ``OPS_MAP_ESRI_RASTER_MAX_ZOOM``;
 * camera matches that cap so MapLibre does not over-fetch non-existent zoom levels.
 * ``OPS_MAP_MAX_ZOOM_SATELLITE`` = UI / marker fly-to clamp (historical name; applies to all basemap modes).
 * Min zoom avoids zooming into empty void around the 2D plane when tiles disappear at world scale.
 */

/** Native Esri World Imagery LOD cap (network); equals max camera zoom for this basemap. */
export const OPS_MAP_ESRI_RASTER_MAX_ZOOM = 19;

/** Hard cap for map camera (markers / UI / ``clampOpsMapZoom``). */
export const OPS_MAP_MAX_ZOOM_SATELLITE = 19;

/**
 * Allow multi-country / multi-region overview (was effectively ≥12.4 via fenceSite).
 * Still bounds the empty mercator void; world wraps horizontally.
 */
export const OPS_MAP_MIN_ZOOM = 1.2;

/** Street / satellite basemap: fleet / search / fly actions stay 2D for readability + lower GPU load. */
export const OPS_MAP_UI_PITCH = 0;
export const OPS_MAP_UI_BEARING = 0;

/**
 * Default map home: Malaysia-first (product).
 * Center slightly inland of KL so Peninsular Malaysia fills the viewport at region zoom.
 */
export const OPS_MAP_DEFAULT_CENTER_LNG = 101.9758;
export const OPS_MAP_DEFAULT_CENTER_LAT = 4.2105;
export const OPS_MAP_DEFAULT_ZOOM_REGION = 5.6;

/** @param {number | string | undefined | null} z */
export function clampOpsMapZoom(z) {
  const n = Number(z);
  if (!Number.isFinite(n)) return Math.min(14, OPS_MAP_MAX_ZOOM_SATELLITE);
  return Math.max(OPS_MAP_MIN_ZOOM, Math.min(OPS_MAP_MAX_ZOOM_SATELLITE, n));
}

/**
 * Initial center/zoom when no deep-link flyTo — always Malaysia-first for this product.
 * @returns {{ lng: number; lat: number; zoom: number }}
 */
export function getOpsMapDefaultCenterZoom() {
  return {
    lng: OPS_MAP_DEFAULT_CENTER_LNG,
    lat: OPS_MAP_DEFAULT_CENTER_LAT,
    zoom: OPS_MAP_DEFAULT_ZOOM_REGION,
  };
}

/**
 * Peninsular + East Malaysia + light buffer (product home).
 * Auto-fit on Home map only uses points inside this box; otherwise stay Malaysia-first.
 * @param {number} lat
 * @param {number} lng
 */
export function isOpsMapMalaysiaRegion(lat, lng) {
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return false;
  return la >= -1.5 && la <= 8.5 && ln >= 99 && ln <= 120;
}

/**
 * Read ``#/overview?lat=…&lng=…&zoom=…`` from the hash router (legacy ``#/ops-map?…`` hash deleted tip559).
 * @returns {{ lat: number; lng: number; zoom: number } | null}
 */
export function readOpsMapFlyToFromWindow() {
  if (typeof window === "undefined") return null;
  const rq = window.__routeQuery;
  if (!(rq instanceof URLSearchParams)) return null;
  // Number("") === 0 — empty query keys must NOT become world-origin flyTo.
  const latRaw = String(rq.get("lat") ?? "").trim();
  const lngRaw = String(rq.get("lng") ?? "").trim();
  if (!latRaw || !lngRaw) return null;
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  // Accidental 0,0 is never a product deep-link for this console.
  if (Math.abs(lat) < 1e-6 && Math.abs(lng) < 1e-6) return null;
  const zoomRaw = String(rq.get("zoom") ?? "").trim();
  const zRaw = zoomRaw ? Number(zoomRaw) : NaN;
  const zoom = clampOpsMapZoom(Number.isFinite(zRaw) ? zRaw : 16);
  return { lat, lng, zoom };
}

/** @returns {string} legacy ``pinDevice`` hash param (stripped on Home map load), or "" */
export function readOpsMapPinDeviceFromWindow() {
  if (typeof window === "undefined") return "";
  const rq = window.__routeQuery;
  if (!(rq instanceof URLSearchParams)) return "";
  return String(rq.get("pinDevice") || "").trim();
}

/** Remove only ``pinDevice`` from the hash query (keep lat/lng/zoom if present). */
export function stripOpsMapPinDeviceFromHash() {
  if (typeof window === "undefined" || typeof history === "undefined") return;
  try {
    let h = location.hash || "";
    const qm = h.indexOf("?");
    if (qm < 0) return;
    const base = h.slice(0, qm);
    const qs = new URLSearchParams(h.slice(qm + 1));
    if (!qs.has("pinDevice")) return;
    qs.delete("pinDevice");
    const tail = qs.toString();
    const next = tail ? `${base}?${tail}` : base;
    history.replaceState(history.state, "", `${location.pathname}${location.search}${next}`);
    window.__routeQuery = new URLSearchParams(tail);
  } catch (_) {}
}

/** Remove ``lat``/``lng``/``zoom`` from hash (after camera applied); keep other params. */
export function stripOpsMapFlyQueryFromHash() {
  if (typeof window === "undefined" || typeof history === "undefined") return;
  try {
    const h = location.hash || "";
    const qm = h.indexOf("?");
    if (qm < 0) return;
    const base = h.slice(0, qm);
    const qs = new URLSearchParams(h.slice(qm + 1));
    const had = qs.has("lat") || qs.has("lng") || qs.has("zoom");
    qs.delete("lat");
    qs.delete("lng");
    qs.delete("zoom");
    if (!had) return;
    const tail = qs.toString();
    const next = tail ? `${base}?${tail}` : base;
    history.replaceState(history.state, "", `${location.pathname}${location.search}${next}`);
    window.__routeQuery = new URLSearchParams(tail);
  } catch (_) {}
}

/** Strip fly query so refresh does not re-zoom. */
export function replaceHashOpsMapPlain() {
  try {
    if (typeof history === "undefined" || typeof location === "undefined") return;
    const path = `${location.pathname}${location.search}`;
    history.replaceState(history.state, "", `${path}#/overview`);
    window.__routeQuery = new URLSearchParams("");
  } catch (_) {}
}
